import { Button, Icon, InputGroup, useToast } from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import { MdFileUpload } from "react-icons/md";

import { bytesToMegabytes } from "src/common/util";
import {
  useImportIntercomsMutation,
  useIntercomsQuery,
} from "src/routes/ProductUnits/Intercoms/queries";

const MAX_FILE_SIZE_MB = 5;

export const IntercomUploadButton = () => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const intercoms = useIntercomsQuery(false);

  const onSuccess = useCallback(
    (data: string[]) => {
      if (!data.length) {
        toast({
          title: "Intercom units successfully imported",
          status: "success",
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to import the following intercoms",
          description: data.join(", "),
          status: "warning",
          duration: null,
          isClosable: true,
        });
      }

      intercoms.refetch();
    },
    [intercoms, toast]
  );

  const onError = useCallback(() => {
    toast({
      title: "Failed to import intercoms",
      status: "error",
      isClosable: true,
    });
  }, [toast]);

  const importMutation = useImportIntercomsMutation(onSuccess, onError);

  const onClick = useCallback(() => inputRef.current?.click(), [inputRef]);
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (bytesToMegabytes(file.size) > MAX_FILE_SIZE_MB) {
        toast({
          title: "File is too large",
          description: `Please upload a file smaller than ${MAX_FILE_SIZE_MB}MB`,
          status: "error",
          isClosable: true,
        });
        return;
      }

      const data = new FormData();
      data.append("file", file);
      importMutation.mutate(data);
    },
    [importMutation]
  );

  return (
    <InputGroup onClick={onClick} width="unset">
      <input
        type="file"
        id="intercom-upload-file"
        accept=".csv"
        disabled={importMutation.isLoading}
        multiple={false}
        hidden
        ref={(e) => {
          inputRef.current = e;
        }}
        onChange={onChange}
      />
      <Button
        colorScheme="brand.blue"
        isDisabled={importMutation.isLoading}
        leftIcon={<Icon as={MdFileUpload} fontSize="20px" />}
        variant="outline"
      >
        CSV Upload
      </Button>
    </InputGroup>
  );
};
